"use client";

import { RichText } from "@payloadcms/richtext-lexical/react";
import { SocialSharing } from "@repo/ui/social-sharing";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { ReviewsList } from "@/features/reviews/ui";
import {
  buildShippingVars,
  getHeroTrustLineKey,
  toTransitTemplateVars,
} from "@/features/shipping/shared/shippingVars";
import { useShippingSettings } from "@/features/shipping/ui/hooks/useShippingSettings";
import { CommunityTryOnsGallery, VirtualTryOnTrigger } from "@/features/virtual-try-on/ui";
import { Link } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { useCountryStore } from "@/lib/client/stores/useCountryStore";
import { type ICountry, placeholderImage } from "@/lib/shared/constants";
import { buildProductSeoHighlights } from "@/lib/shared/seoContent";
import type {
  MediaDisplay,
  ProductForClient,
  ProductVariantForClient,
} from "@/lib/shared/types/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Box,
  HStack,
  Separator,
  StarRating,
  Text,
  VStack,
} from "@/ui";
import { getProductReviewSummary } from "../../shared/productReviewSummary";
import {
  buildProductShareHashtags,
  buildProductShareUrl,
  getPrimaryShareTag,
} from "../../shared/socialShare";
import { ImageSlider } from "./ImageSlider";
import { MediaLightbox } from "./MediaLightbox";
import { ProductVariants } from "./ProductVariants";

interface IProductDetailsProps {
  product: ProductForClient;
  initialCountry?: ICountry;
}

export const ProductDetails = (props: IProductDetailsProps) => {
  const { product, initialCountry } = props;
  const locale = useLocale();
  const { countries, country } = useCountryStore();
  const displayCountry = initialCountry && countries == null ? initialCountry : country;
  const productDetailsT = useTranslations("product_details");
  const heroT = useTranslations("hero");
  const generalT = useTranslations("general");
  const { settings } = useShippingSettings();
  const shippingVars = toTransitTemplateVars(
    buildShippingVars(settings, { countryCode: displayCountry.countryCode }),
  );
  const trustLine = heroT(getHeroTrustLineKey(displayCountry.countryCode), shippingVars);
  const seoHighlights = buildProductSeoHighlights(product);
  const reviewSummary = getProductReviewSummary(product.reviews);

  // Product should always have at least one variant
  const initialVariant =
    product.variants && product.variants.length > 0 ? product.variants[0] : null;

  if (!initialVariant) {
    throw new Error("Product must have at least one variant");
  }

  const [selectedVariant, setSelectedVariant] = useState<ProductVariantForClient>(initialVariant);
  const [hasUserSelectedVariant, setHasUserSelectedVariant] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Combine images and video for lightbox
  const displayItems: MediaDisplay[] = useMemo(() => {
    const items = product.images?.length ? [...product.images] : [];
    if (product.video) {
      items.push(product.video);
    }
    return items;
  }, [product.images, product.video]);

  const productShareUrl = buildProductShareUrl(locale, product.sku);
  const productShareHashtags = buildProductShareHashtags(product.sku);
  const productShareImage =
    product.images?.[selectedVariant.imageIndex ?? 0]?.src ??
    product.images?.[0]?.src ??
    placeholderImage.src;

  const handleVariantChange = (variant: ProductVariantForClient) => {
    setSelectedVariant(variant);
    setHasUserSelectedVariant(true);
  };

  const handleMediaClick = (index: number) => {
    trackEvent(AnalyticsEvents.PRODUCT_IMAGE_ZOOMED, {
      product_id: product.id,
      sku: product.sku,
      image_index: index,
    });
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  // Track product view on mount
  useEffect(() => {
    trackEvent(AnalyticsEvents.PRODUCT_VIEWED, {
      product_id: product.id,
      sku: product.sku,
      name: product.name,
      price: initialVariant?.price,
      variant_id: initialVariant?.variant.id,
    });
  }, [product.id, product.sku, product.name, initialVariant]);

  return (
    <Box className="container-luxury">
      <Box className="lg:grid lg:grid-cols-12 lg:gap-x-12 xl:gap-x-16 lg:py-8">
        {/* Left column - Images and try-on */}
        <Box className="lg:col-span-7">
          <VStack className="gap-6">
            <ImageSlider
              images={product.images?.length ? product.images : [placeholderImage]}
              video={product.video}
              hasThumb={true}
              canZoom={true}
              onMediaClick={handleMediaClick}
              jumpToIndex={hasUserSelectedVariant ? (selectedVariant.imageIndex ?? 0) : 0}
              width={900}
            />
            <VStack className="gap-3">
              <VirtualTryOnTrigger
                product={product}
                initialVariantId={selectedVariant.variant.id}
                fullWidth
              />
              <CommunityTryOnsGallery sku={product.sku} />
            </VStack>
          </VStack>
        </Box>

        {/* Media Lightbox */}
        <MediaLightbox
          isOpen={lightboxOpen}
          onClose={handleCloseLightbox}
          items={displayItems}
          initialIndex={lightboxIndex}
          videoIndex={product.video ? (product.images?.length ?? 0) : undefined}
        />

        {/* Product details - refined layout */}
        <Box className="mt-12 lg:mt-0 lg:col-span-5">
          <VStack className="lg:sticky lg:top-32 lg:h-fit gap-6">
            {/* Category label */}
            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              {productDetailsT("default_category")}
            </span>

            {/* Product Name - elegant serif */}
            <h1 className="font-product-title text-3xl lg:text-4xl font-medium text-foreground">
              {product.name}
            </h1>

            {reviewSummary ? (
              <HStack className="items-center gap-2 text-sm text-muted-foreground">
                <StarRating rating={Math.floor(reviewSummary.averageRating)} />
                <span>
                  {reviewSummary.averageRating} ({reviewSummary.count})
                </span>
              </HStack>
            ) : null}

            {/* Stock status - refined badges */}
            <HStack className="items-center gap-4">
              {selectedVariant.stock === 0 ? (
                <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-red-600">
                  {generalT("out_of_stock")}
                </span>
              ) : selectedVariant.stock && selectedVariant.stock >= 10 ? (
                <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-green-600">
                  {generalT("in_stock")}
                </span>
              ) : (
                <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-amber-600">
                  {generalT("only")} {selectedVariant.stock} {generalT("left")}
                </span>
              )}
            </HStack>

            <Separator className="bg-border/30" />

            {/* Price - elegant display */}
            <Box>
              {selectedVariant.onSalePrice ? (
                <HStack className="items-baseline gap-3">
                  <span className="font-price text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                    {displayCountry.symbol}
                    {Number(selectedVariant.onSalePrice * displayCountry.exchangeRate).toFixed(2)}
                  </span>
                  <span className="font-price text-lg text-muted-foreground line-through">
                    {displayCountry.symbol}
                    {Number(selectedVariant.price * displayCountry.exchangeRate).toFixed(2)}
                  </span>
                  <span className="px-2 py-1 text-[10px] font-medium uppercase tracking-[0.15em] bg-destructive text-white">
                    {generalT("sale")}
                  </span>
                </HStack>
              ) : (
                <span className="font-price text-3xl lg:text-4xl font-medium text-foreground tracking-tight">
                  {displayCountry.symbol}
                  {Number(selectedVariant.price * displayCountry.exchangeRate).toFixed(2)}
                </span>
              )}
            </Box>

            {/* Market-aware shipping info line */}
            <p className="text-xs text-muted-foreground">{trustLine}</p>

            {/* Variants - includes Add to Cart button */}
            <ProductVariants
              product={product}
              selectedVariant={selectedVariant}
              setSelectedVariant={handleVariantChange}
            />

            <SocialSharing
              url={productShareUrl}
              title={product.name}
              hashtags={productShareHashtags}
              primaryHashtag={getPrimaryShareTag(productShareHashtags)}
              className="w-full flex-wrap items-center gap-2"
              image={productShareImage}
              description={product.name}
              label={generalT("share")}
            />

            <Separator className="bg-border/30" />

            {/* Product Description */}
            {product.description && (
              <Box className={"prose prose-sm max-w-none"}>
                <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground block mb-3">
                  {productDetailsT("details")}
                </span>
                <RichText data={product.description} />
              </Box>
            )}

            {seoHighlights.length > 0 ? (
              <Box className="w-full rounded-xl border border-border/40 bg-muted/20 p-4">
                <Text
                  as="h2"
                  className="mb-3 text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Product highlights
                </Text>
                <ul className="space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {seoHighlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </Box>
            ) : null}

            {/* Additional details - luxury accordion */}
            <Accordion
              type={"multiple"}
              className={"w-full"}
            >
              <AccordionItem
                value={productDetailsT("shippingAndReturn")}
                className="border-b border-border/30"
              >
                <AccordionTrigger className="text-sm font-medium uppercase tracking-[0.1em] hover:no-underline py-4">
                  {productDetailsT("shippingAndReturn")}
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <Text className="text-muted-foreground leading-relaxed">
                    {productDetailsT("shippingDetails", shippingVars)}
                  </Text>
                  <Link
                    href={"/customerServices"}
                    className={"inline-block mt-3 text-sm text-primary hover:underline"}
                  >
                    {productDetailsT("more_details")}
                  </Link>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value={productDetailsT("care")}
                className="border-b border-border/30"
              >
                <AccordionTrigger className="text-sm font-medium uppercase tracking-[0.1em] hover:no-underline py-4">
                  {productDetailsT("care")}
                </AccordionTrigger>
                <AccordionContent className="pb-4 prose prose-sm">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: productDetailsT.raw("care_detail"),
                    }}
                  />
                  <Link
                    href={"/customerServices"}
                    className={"text-sm text-primary hover:underline"}
                  >
                    {productDetailsT("more_details")}
                  </Link>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </VStack>
        </Box>
      </Box>
      <Box
        className="border-t border-border/30 py-12"
        id="reviews"
      >
        <Text
          as="h2"
          className="mb-6 text-xl font-medium"
        >
          {productDetailsT("reviews")}
        </Text>
        <ReviewsList productId={product.id} />
      </Box>
    </Box>
  );
};
